import { useEffect, useId, useRef, useState } from 'react';
import type { FormFieldDef } from '../../types';
import { t } from '../../i18n';
import { normalizeDateValue } from '../../utils/form-renderer';
import { parseFileReference, resolveFileFieldValue, uploadForFormField } from '../../utils/file-field';
import type { McpFormProps } from './types';

function buildInitialValues(
  fields: FormFieldDef[],
  initialValues?: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const raw = initialValues?.[f.name] ?? f.defaultValue;
    if (f.type === 'checkbox') out[f.name] = Boolean(raw);
    else if (f.type === 'date') out[f.name] = normalizeDateValue(raw);
    else out[f.name] = raw != null ? String(raw) : '';
  }
  return out;
}

/** "[File: name (meta)]" -> "name (meta)" — parseFileReference expects the inside of the brackets. */
function extractBracketInner(marker: string): string {
  const match = marker.match(/^\[File:\s*(.+)\]$/);
  return match ? match[1] : marker;
}

function buildInitialFileStatus(
  fields: FormFieldDef[],
  values: Record<string, unknown>,
): Record<string, { text: string; error?: boolean }> {
  const out: Record<string, { text: string; error?: boolean }> = {};
  for (const f of fields) {
    if (f.type !== 'file') continue;
    const raw = values[f.name];
    if (typeof raw === 'string' && raw.length > 0) {
      const filename = parseFileReference(extractBracketInner(raw)).filename;
      out[f.name] = { text: filename || 'File selected' };
    }
  }
  return out;
}

// A required checkbox is never flagged - its value is always a real boolean
// (never '' / undefined / null). Pre-existing quirk from the original validateForm(),
// preserved rather than silently fixed since that's a separate, unscoped behavior change.
function validateFields(values: Record<string, unknown>, fields: FormFieldDef[]): string[] {
  return fields
    .filter((f) => f.required)
    .filter((f) => values[f.name] === '' || values[f.name] == null)
    .map((f) => f.name);
}

function collectSubmitValues(values: Record<string, unknown>, fields: FormFieldDef[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const v = values[f.name];
    if (f.type === 'checkbox') {
      out[f.name] = Boolean(v);
      continue;
    }
    if (f.type === 'number') {
      if (v !== '' && v != null) out[f.name] = Number(v);
      continue;
    }
    if (v !== '' && v != null) out[f.name] = v;
  }
  return out;
}

export function McpForm({
  fields,
  initialValues,
  submitLabel,
  cancelLabel,
  onCancel,
  onSubmit,
  formType,
  toolName,
  rootAttributes,
  autoFocusFirstField,
}: McpFormProps) {
  const uid = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const fileInputRefs = useRef(new Map<string, HTMLInputElement>());
  const [values, setValues] = useState<Record<string, unknown>>(() => buildInitialValues(fields, initialValues));
  const [errorFields, setErrorFields] = useState<Set<string>>(new Set());
  const uploadStateRef = useRef(new Map<string, { seq: number; controller: AbortController }>());
  const [uploadingFields, setUploadingFields] = useState<Set<string>>(new Set());
  const [fileStatus, setFileStatus] = useState<Record<string, { text: string; error?: boolean }>>(
    () => buildInitialFileStatus(fields, values),
  );
  const [filePreview, setFilePreview] = useState<Record<string, string | undefined>>({});
  const previewUrlsRef = useRef(new Set<string>());

  useEffect(() => {
    if (!autoFocusFirstField) return;
    (rootRef.current?.querySelector('input, select') as HTMLElement | null)?.focus();
  }, [autoFocusFirstField]);

  useEffect(() => () => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const setFieldValue = (name: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (field: FormFieldDef, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const previous = uploadStateRef.current.get(field.name);
    previous?.controller.abort();
    const seq = (previous?.seq ?? 0) + 1;
    const controller = new AbortController();
    uploadStateRef.current.set(field.name, { seq, controller });

    const previousStatus = fileStatus[field.name]?.text;
    const previousPreviewUrl = filePreview[field.name];
    const newPreviewUrl = URL.createObjectURL(file);
    previewUrlsRef.current.add(newPreviewUrl);
    setUploadingFields((prev) => new Set(prev).add(field.name));
    setFileStatus((prev) => ({ ...prev, [field.name]: { text: 'Uploading…' } }));
    setFilePreview((prev) => ({ ...prev, [field.name]: newPreviewUrl }));

    uploadForFormField(file, controller.signal)
      .then(({ attachment }) => {
        if (uploadStateRef.current.get(field.name)?.seq !== seq) return;
        const resolved = resolveFileFieldValue({ name: field.name, label: field.label }, undefined, attachment);
        setFieldValue(field.name, resolved);
        setFileStatus((prev) => ({ ...prev, [field.name]: { text: attachment.filename } }));
        if (previousPreviewUrl && previousPreviewUrl !== newPreviewUrl) {
          URL.revokeObjectURL(previousPreviewUrl);
          previewUrlsRef.current.delete(previousPreviewUrl);
        }
      })
      .catch(() => {
        if (uploadStateRef.current.get(field.name)?.seq !== seq) return;
        URL.revokeObjectURL(newPreviewUrl);
        previewUrlsRef.current.delete(newPreviewUrl);
        if (previousStatus) {
          setFileStatus((prev) => ({
            ...prev,
            [field.name]: { text: `Couldn't upload the new file — keeping '${previousStatus}'.`, error: true },
          }));
          setFilePreview((prev) => ({ ...prev, [field.name]: previousPreviewUrl }));
        } else {
          setFieldValue(field.name, '');
          setFileStatus((prev) => ({ ...prev, [field.name]: { text: 'Upload failed — try again.', error: true } }));
          setFilePreview((prev) => ({ ...prev, [field.name]: undefined }));
        }
      })
      .finally(() => {
        if (uploadStateRef.current.get(field.name)?.seq === seq) {
          setUploadingFields((prev) => {
            const next = new Set(prev);
            next.delete(field.name);
            return next;
          });
        }
      });
  };

  const handleRemoveFile = (fieldName: string) => {
    setFieldValue(fieldName, '');
    setFileStatus((prev) => ({ ...prev, [fieldName]: { text: 'No file selected' } }));
    setFilePreview((prev) => {
      const url = prev[fieldName];
      if (url) {
        URL.revokeObjectURL(url);
        previewUrlsRef.current.delete(url);
      }
      return { ...prev, [fieldName]: undefined };
    });
  };

  const handleSubmit = () => {
    if (uploadingFields.size > 0) return;
    const errors = validateFields(values, fields);
    if (errors.length > 0) {
      setErrorFields(new Set(errors));
      return;
    }
    setErrorFields(new Set());
    onSubmit(collectSubmitValues(values, fields));
  };

  return (
    <div
      ref={rootRef}
      className="mcp-form"
      data-form-type={formType}
      data-tool-name={toolName || undefined}
      {...rootAttributes}
    >
      {fields.map((field) => {
        const fieldId = `${uid}-${field.name}`;
        const hasError = errorFields.has(field.name);
        return (
          <div
            key={field.name}
            className={`form-field${field.required ? ' form-field-required' : ''}${hasError ? ' has-error' : ''}`}
          >
            <label htmlFor={fieldId}>{field.label}</label>
            {field.description ? <label htmlFor={fieldId}>{field.description}</label> : null}
            {field.type === 'select' && field.options ? (
              <select
                id={fieldId}
                value={String(values[field.name] ?? '')}
                onChange={(e) => setFieldValue(field.name, e.target.value)}
              >
                <option value=""></option>
                {field.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : field.type === 'checkbox' ? (
              <input
                type="checkbox"
                id={fieldId}
                checked={Boolean(values[field.name])}
                onChange={(e) => setFieldValue(field.name, e.target.checked)}
              />
            ) : field.type === 'number' ? (
              <input
                type="number"
                id={fieldId}
                value={values[field.name] != null ? String(values[field.name]) : ''}
                onChange={(e) => setFieldValue(field.name, e.target.value)}
                placeholder={field.placeholder}
              />
            ) : field.type === 'date' ? (
              <input
                type="date"
                id={fieldId}
                value={normalizeDateValue(values[field.name])}
                onChange={(e) => setFieldValue(field.name, e.target.value)}
              />
            ) : field.type === 'file' ? (
              <div
                className={`form-field-file${filePreview[field.name] ? ' has-preview' : ''}`}
                data-field-name={field.name}
              >
                <button
                  type="button"
                  className="form-file-btn"
                  disabled={uploadingFields.has(field.name)}
                  onClick={() => fileInputRefs.current.get(field.name)?.click()}
                >
                  Choose File
                </button>
                <input
                  type="file"
                  accept="image/*"
                  ref={(el) => {
                    if (el) fileInputRefs.current.set(field.name, el);
                    else fileInputRefs.current.delete(field.name);
                  }}
                  className="form-file-input"
                  tabIndex={-1}
                  aria-hidden="true"
                  onChange={(e) => handleFileChange(field, e)}
                />
                <span className="form-file-status" aria-live="polite">
                  {fileStatus[field.name]?.text ?? 'No file selected'}
                </span>
                {fileStatus[field.name]?.text && fileStatus[field.name]?.text !== 'No file selected' && (
                  <button
                    type="button"
                    className="form-file-remove"
                    aria-label="Remove selected file"
                    onClick={() => handleRemoveFile(field.name)}
                  >
                    &times;
                  </button>
                )}
                {filePreview[field.name] && (
                  <div className="form-file-preview">
                    <img
                      className="form-file-preview-img"
                      src={filePreview[field.name]}
                      alt={field.label}
                      onClick={() => fileInputRefs.current.get(field.name)?.click()}
                    />
                    <button
                      type="button"
                      className="form-file-remove form-file-preview-remove"
                      aria-label="Remove selected file"
                      onClick={() => handleRemoveFile(field.name)}
                    >
                      &times;
                    </button>
                    <span className="form-file-status form-file-preview-status" aria-live="polite">
                      {fileStatus[field.name]?.text ?? 'No file selected'}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <input
                type="text"
                id={fieldId}
                value={values[field.name] != null ? String(values[field.name]) : ''}
                onChange={(e) => setFieldValue(field.name, e.target.value)}
                placeholder={field.placeholder}
              />
            )}
            {hasError && <span className="form-field-error">{t('formRequired')}</span>}
          </div>
        );
      })}
      <div className="form-actions">
        <button className="form-submit" type="button" onClick={handleSubmit} disabled={uploadingFields.size > 0}>
          {submitLabel}
        </button>
        {onCancel && cancelLabel && (
          <button className="form-cancel" type="button" onClick={onCancel}>{cancelLabel}</button>
        )}
      </div>
      {uploadingFields.size > 0 && (
        <span className="form-uploading-hint">Please wait for the image upload to finish.</span>
      )}
    </div>
  );
}
